/**
 * Application shell.
 *
 * Responsibility: own the single analysis worker (wrapped in AnalysisClient)
 * and all session state -- the loaded dataset summary, the active analysis
 * parameters, the latest RPP/QC/segments/targets results, the line
 * selection, and which screen is showing -- and route between the six
 * screens in PLAN.md ("UI walkthrough"). Screens receive plain props and
 * callbacks; only this file talks to the worker.
 *
 * Loading: the genotype `File` goes to the worker as-is, cloned by
 * reference rather than read here, so the main thread never holds its bytes;
 * the worker streams it (docs/adr/0012). samples.csv and markers.csv are
 * transferred as ArrayBuffers.
 *
 * BrAPI (docs/adr/0015): `runLoad` takes a `LoadRequest`, 'load', 'loadBrapi'
 * or 'loadDemo' (docs/adr/0017, whose files the worker fetches); for
 * 'loadBrapi' the worker fetches the variant set itself, and
 * `brapiLoading` is true from dispatch until its 'loaded' or error, which is
 * what shows the Upload screen's Cancel button. `cancelBrapi` posts
 * 'cancelBrapi', which the worker handles out of band (not queued behind the
 * load it cancels). `fetchCallSets` issues 'brapiCallSets' for the call-set
 * table download; like `runCompare` it is not on the sequence counter, and
 * its errors go back to the Upload screen rather than the app alert.
 *
 * Sequencing after a successful 'load': request 'rpp', then 'qc' (which
 * needs a LineRpp[] and so must follow rpp), then 'segmentsAll', awaited in
 * order so a failure is attributable to one step; then navigate to Summary.
 * Editing a parameter after load does not re-parse: changing rpp params
 * re-requests rpp (+ qc, which depends on it); changing segment params
 * re-requests segmentsAll (+ targets, if any specs are active); changing QC
 * thresholds re-requests qc only.
 *
 * Overlapping chains: a `useRef` sequence counter is bumped at the start of
 * every load/param-change chain. Each chain checks the counter against its
 * own value after every awaited step and bails out (no `set*` call, no
 * `busy` clear) once a later chain has started, so the last change always
 * wins and state is never a mix of two parameter sets. A chain that fails
 * while still current rolls `params` back to the value it had before that
 * chain's edit and reports the error; results already on screen are left as
 * they were. `runLoad` reads params through `paramsRef`, which always holds
 * the latest committed value, rather than the closure captured when the
 * Load button was clicked. Parameter inputs are disabled (via `busy`) while
 * any chain is in flight.
 *
 * Keyboard navigation (M2, moved into the rail in M2.5 phase 5): the screen
 * nav is a roving-tabindex toolbar (role="toolbar") -- see ui/shell/Rail.tsx,
 * which owns SCREENS, the arrow-key handling and the blocked-step rule. A
 * "Skip to main content" link is still the first focusable element on the
 * page, targeting the focusable <main>. The global :focus-visible outline
 * that replaces the browser default (not reliably visible against this app's
 * background) is now a rule in ui/base.css rather than an inline <style>
 * block here.
 *
 * Shell (M2.5 phase 5): a two-column grid: the skip link, the masthead, the
 * rail and the content column, which ends in the footer (ui/shell/shell.css). `railCollapsed` is null while the rail follows the
 * screen -- collapsed on the genotype view, where horizontal room is the
 * scarce resource, expanded elsewhere -- and a user toggle pins a boolean
 * that wins for the rest of the session. Nothing here carries a colour or a
 * dimension: every one is a token named by a stylesheet.
 *
 * Compare (M2): `compare` holds the latest 'compare' worker result;
 * `runCompare` issues that request. Like `handleApplyTargets`, it is
 * deliberately not on the shared sequence counter (the Compare screen is
 * reachable at any time and a failure must not disturb an in-flight param
 * chain or clobber the previous result), and it is cleared on every new
 * load.
 *
 * Shared line model (M2.5): `lineRows` is the one row-per-candidate model
 * (ui/lines/line-rows.ts); `lineSort` and `lineFilter` are the one display
 * order (ui/lines/line-order.ts) that the Lines table and the graphical
 * genotype view both read, so sorting or filtering from either screen moves
 * the other. The canvas draws the visible rows in display order and the
 * HTML report inherits that (docs/adr/0009, amended 2026-09-11); the M2
 * rule "the selection, else every candidate" is now `lineFilter.selectedOnly`.
 * Selection changes from either screen speak only for the visible rows
 * (`selectAllVisible`/`selectNone`), leaving hidden selected ids alone.
 *
 * Density (M2.5 phase 4): `density` is session state here and is rendered
 * as `data-density` on the root <div>, where tokens.css re-points
 * --row-height at the compact, default or comfortable height. It is never
 * reset, including on a new load, and is not persisted across reloads.
 *
 * Export (M2): the 'classes' fetch effect below (keyed on `screen` and
 * `classesReqKey`) also runs on the Export screen, not only Graphical
 * genotypes, since the HTML report's per-line figures are rendered from the
 * same worker-fetched `classesData` rather than a second request. That key
 * is the *set* of visible ids, sorted (ui/lines/classes-order.ts), so
 * changing only the sort order reuses the fetched data and the reorder
 * happens on the main thread: a sort never issues a worker request
 * (docs/adr/0001). Everything else ExportScreen needs (rpp, qc,
 * segmentsByCandidate, targets, params, gapCriterion, compare, busy) is
 * already tracked here and passed straight through; `busy` lets it disable
 * the one export that issues its own worker request (discordant-markers
 * CSV) while a load or parameter chain could still replace the dataset that
 * request would run against.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { config } from './config.ts';
import type { GapCriterion } from './core/segments.ts';
import type {
  DonorSegment,
  LineRpp,
  PairwiseDiff,
  QcReport,
  SampleRecord,
  TargetCheck,
  TargetRegion,
} from './core/types.ts';
import { classesRequestKey, permuteClassesData } from './ui/lines/classes-order.ts';
import type { Density } from './ui/lines/LineActionBar.tsx';
import { EMPTY_LINE_FILTER, lineCounts, orderLineRows } from './ui/lines/line-order.ts';
import type { LineFilter, LineSort } from './ui/lines/line-order.ts';
import { buildLineRows } from './ui/lines/line-rows.ts';
import { Footer } from './ui/shell/Footer.tsx';
import { Masthead } from './ui/shell/Masthead.tsx';
import { Rail } from './ui/shell/Rail.tsx';
import type { Screen } from './ui/shell/Rail.tsx';
import './ui/shell/shell.css';
import { CompareScreen } from './ui/screens/CompareScreen.tsx';
import { ExportScreen } from './ui/screens/ExportScreen.tsx';
import { GenotypeViewScreen } from './ui/screens/GenotypeViewScreen.tsx';
import { LineTableScreen } from './ui/screens/LineTableScreen.tsx';
import { SummaryScreen } from './ui/screens/SummaryScreen.tsx';
import type { AnalysisParams, LoadedState, LoadRequest } from './ui/screens/UploadScreen.tsx';
import { UploadScreen } from './ui/screens/UploadScreen.tsx';
import {
  demoLoadPayload,
  parseDemoParam,
  unknownCropMessage,
  unknownDemoMessage,
} from './ui/demo.ts';
import { AnalysisClient } from './workers/client.ts';
import type { GenotypeClassesData } from './workers/protocol.ts';
import type { BrapiCallSet, BrapiSource } from './io/brapi.ts';

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// Stable empty fallbacks for CompareScreen's `samples`/`chromosomeOrder`
// props while nothing is loaded, so its effect (keyed on the `samples`
// array's identity) does not re-fire on every unrelated App render.
const NO_SAMPLES: SampleRecord[] = [];
const NO_CHROMOSOMES: string[] = [];

export function App() {
  const [screen, setScreen] = useState<Screen>('upload');
  const [params, setParams] = useState<AnalysisParams>({
    rpp: config.rpp,
    segments: config.segments,
    qc: config.qc,
  });
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [rpp, setRpp] = useState<LineRpp[] | null>(null);
  const [qc, setQc] = useState<QcReport | null>(null);
  const [segmentsByCandidate, setSegmentsByCandidate] = useState<DonorSegment[][] | null>(null);
  const [gapCriterion, setGapCriterion] = useState<GapCriterion | null>(null);
  const [targetSpecs, setTargetSpecs] = useState<string[]>([]);
  const [targets, setTargets] = useState<{ regions: TargetRegion[]; checks: TargetCheck[] } | null>(
    null,
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lineSort, setLineSort] = useState<LineSort | null>(null);
  const [lineFilter, setLineFilter] = useState<LineFilter>(EMPTY_LINE_FILTER);
  const [compare, setCompare] = useState<PairwiseDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [brapiLoading, setBrapiLoading] = useState(false);
  const [density, setDensity] = useState<Density>('default');
  // null = follow the screen (see the header comment); a boolean is a user
  // choice and wins for the rest of the session.
  const [railCollapsed, setRailCollapsed] = useState<boolean | null>(null);

  const [classesData, setClassesData] = useState<GenotypeClassesData | null>(null);
  const [classesKey, setClassesKey] = useState<string | null>(null);
  const [classesLoading, setClassesLoading] = useState(false);

  const mainRef = useRef<HTMLElement | null>(null);

  const clientRef = useRef<AnalysisClient | null>(null);
  // Always holds the latest committed `params`, so runLoad reads the
  // current value instead of the click-time closure.
  const paramsRef = useRef<AnalysisParams>(params);
  // Bumped at the start of every load/param-change chain; a chain compares
  // its own snapshot against the live value to detect it has been
  // superseded (see the module header comment).
  const requestSeqRef = useRef(0);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    const worker = new Worker(new URL('./workers/analysis.worker.ts', import.meta.url), {
      type: 'module',
    });
    const client = new AnalysisClient(worker);
    clientRef.current = client;
    return () => {
      client.terminate();
      clientRef.current = null;
    };
  }, []);

  // `?demo=<name>` (docs/adr/0017), with an optional `&crop=<id>`: load that
  // demo on arrival under that crop, as the Upload screen's demo button does,
  // or report an unknown demo or crop in the alert.
  // Declared after the worker effect so the client exists. No run-once
  // guard: under StrictMode's effect replay the first chain's worker is
  // terminated and the second chain, on the sequence counter, supersedes it.
  useEffect(() => {
    const demo = parseDemoParam(location.search);
    if (demo.kind === 'unknown') setError(unknownDemoMessage(demo.value));
    if (demo.kind === 'unknownCrop') setError(unknownCropMessage(demo.value));
    if (demo.kind !== 'demo') return;
    void runLoad({
      type: 'loadDemo',
      payload: {
        ...demoLoadPayload(demo.name, import.meta.env.BASE_URL, location.href),
        ...(demo.crop === undefined ? {} : { crop: demo.crop }),
      },
    });
    // runLoad is recreated every render; this effect runs on mount only.
  }, []);

  function getClient(): AnalysisClient {
    const client = clientRef.current;
    if (client === null) throw new Error('worker not ready');
    return client;
  }

  async function runLoad(request: LoadRequest) {
    const seq = ++requestSeqRef.current;
    setError(null);
    setBusy(true);
    try {
      // The genotype File is cloned by reference, not transferred, and read as
      // a stream in the worker (docs/adr/0012); an ArrayBuffer is transferred.
      // A demo load carries URLs only; the worker fetches the files.
      const transfer: Transferable[] = [];
      if (request.type !== 'loadDemo') {
        transfer.push(request.payload.samples);
        if (request.payload.markers !== undefined) transfer.push(request.payload.markers);
      }
      if (request.type === 'load' && request.payload.genotypes instanceof ArrayBuffer) {
        transfer.push(request.payload.genotypes);
      }
      if (request.type === 'loadBrapi') setBrapiLoading(true);
      const client = getClient();
      const loadRes =
        request.type === 'load'
          ? await client.request('load', request.payload, transfer)
          : request.type === 'loadBrapi'
            ? await client.request('loadBrapi', request.payload, transfer)
            : await client.request('loadDemo', request.payload);
      if (requestSeqRef.current !== seq) return;
      setLoaded(loadRes);
      setRpp(null);
      setQc(null);
      setSegmentsByCandidate(null);
      setGapCriterion(null);
      setTargets(null);
      setTargetSpecs([]);
      setSelected(new Set());
      setLineSort(null);
      setLineFilter(EMPTY_LINE_FILTER);
      setCompare(null);
      setClassesData(null);
      setClassesKey(null);

      const rppRes = await client.request('rpp', paramsRef.current.rpp);
      if (requestSeqRef.current !== seq) return;
      setRpp(rppRes.lines);
      const qcRes = await client.request('qc', paramsRef.current.qc);
      if (requestSeqRef.current !== seq) return;
      setQc(qcRes.report);
      const segRes = await client.request('segmentsAll', { params: paramsRef.current.segments });
      if (requestSeqRef.current !== seq) return;
      setSegmentsByCandidate(segRes.byCandidate);
      setGapCriterion(segRes.gapCriterion);
      setScreen('summary');
    } catch (e) {
      if (requestSeqRef.current === seq) setError(errorMessage(e));
    } finally {
      if (requestSeqRef.current === seq) {
        setBusy(false);
        setBrapiLoading(false);
      }
    }
  }

  // Not on the sequence counter, like runCompare; errors propagate to the Upload screen.
  async function fetchCallSets(
    source: BrapiSource,
  ): Promise<{ callSets: BrapiCallSet[]; warnings: string[] }> {
    setBusy(true);
    try {
      const r = await getClient().request('brapiCallSets', { source });
      return { callSets: r.callSets, warnings: r.warnings };
    } finally {
      setBusy(false);
    }
  }

  function cancelBrapi() {
    void getClient().request('cancelBrapi', {});
  }

  async function handleParamsChange(next: AnalysisParams) {
    const prev = params;
    setParams(next);
    if (loaded === null) return;
    const rppChanged = next.rpp !== prev.rpp;
    const segChanged = next.segments !== prev.segments;
    const qcChanged = next.qc !== prev.qc;
    if (!rppChanged && !segChanged && !qcChanged) return;
    const seq = ++requestSeqRef.current;
    setBusy(true);
    setError(null);
    try {
      const client = getClient();
      if (rppChanged) {
        const r = await client.request('rpp', next.rpp);
        if (requestSeqRef.current !== seq) return;
        setRpp(r.lines);
        const q = await client.request('qc', next.qc);
        if (requestSeqRef.current !== seq) return;
        setQc(q.report);
      } else if (qcChanged) {
        const q = await client.request('qc', next.qc);
        if (requestSeqRef.current !== seq) return;
        setQc(q.report);
      }
      if (segChanged) {
        const s = await client.request('segmentsAll', { params: next.segments });
        if (requestSeqRef.current !== seq) return;
        setSegmentsByCandidate(s.byCandidate);
        setGapCriterion(s.gapCriterion);
        if (targetSpecs.length > 0) {
          const t = await client.request('targets', { specs: targetSpecs, params: next.segments });
          if (requestSeqRef.current !== seq) return;
          setTargets({ regions: t.regions, checks: t.checks });
        }
      }
    } catch (e) {
      // Only roll back if this chain is still the current one; a chain that
      // has already been superseded must not clobber a newer edit's params.
      if (requestSeqRef.current === seq) {
        setParams(prev);
        setError(errorMessage(e));
      }
    } finally {
      if (requestSeqRef.current === seq) setBusy(false);
    }
  }

  // Deliberately not on the shared sequence counter: Apply is reachable
  // from the Lines screen at any time, including while a param chain is in
  // flight, and must not cause that chain's own rpp/qc/segments results to
  // be discarded. targetSpecs/targets are updated together only on success,
  // so a failed apply never leaves them mismatched with each other.
  async function handleApplyTargets(specs: string[]) {
    if (loaded === null) return;
    setBusy(true);
    setError(null);
    try {
      const t = await getClient().request('targets', { specs, params: params.segments });
      setTargetSpecs(specs);
      setTargets({ regions: t.regions, checks: t.checks });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Deliberately not on the shared sequence counter, for the same reason as
  // handleApplyTargets above: the Compare screen is reachable at any time,
  // including while a param chain is in flight, and a compare request must
  // not race that chain's own state updates. A failure leaves the previous
  // `compare` result on screen and only reports the error.
  async function runCompare(sampleA: string, sampleB: string, mode: 'informative' | 'all') {
    if (loaded === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await getClient().request('compare', { sampleA, sampleB, mode });
      setCompare(res.diff);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // The shared line model. Rows come out in rpp order (==
  // classification.candidateCols order, the order the worker uses for
  // segmentsByCandidate and 'classes' rows); the filter and sort then
  // decide which lines are visible and in what order, for both the Lines
  // table and the graphical genotype view.
  const lineRows = useMemo(
    () =>
      rpp === null || loaded === null
        ? []
        : buildLineRows({ rpp, samples: loaded.samples, segmentsByCandidate, targets, qc }),
    [rpp, loaded, segmentsByCandidate, targets, qc],
  );
  const visibleLineRows = useMemo(
    () => orderLineRows(lineRows, lineSort, lineFilter, selected),
    [lineRows, lineSort, lineFilter, selected],
  );
  const visibleSampleIds = useMemo(() => visibleLineRows.map((r) => r.sampleId), [visibleLineRows]);
  const counts = useMemo(
    () => lineCounts(lineRows, visibleLineRows, selected),
    [lineRows, visibleLineRows, selected],
  );
  const regions = useMemo(() => targets?.regions ?? [], [targets]);

  // The fetch is keyed on the *set* of visible ids, not their order: a
  // string, so its identity is stable across a reorder and the effect below
  // neither re-runs nor cancels an in-flight request when only the sort
  // changes.
  const classesReqKey = useMemo(() => classesRequestKey(visibleSampleIds), [visibleSampleIds]);

  // Also runs on the Export screen: the HTML report's per-line figures reuse
  // this same worker-fetched class data (see ui/screens/ExportScreen.tsx)
  // rather than issuing a second 'classes' request, so the report shows the
  // same lines the genotype view is showing.
  useEffect(() => {
    if ((screen !== 'genotypes' && screen !== 'export') || loaded === null) return;
    if (classesReqKey === classesKey) return;
    if (classesReqKey === '') {
      setClassesData(null);
      setClassesKey(classesReqKey);
      // A filter that hides every line can land here while an earlier
      // fetch is still in flight; that fetch's `finally` is suppressed by
      // its own cleanup, so clear the flag here or the screen keeps
      // claiming it is loading.
      setClassesLoading(false);
      return;
    }
    // Sorted, exactly as the key is: the request covers the key's set, and
    // display order is applied on the main thread by permuteClassesData.
    const ids = classesReqKey.split('\n');
    let cancelled = false;
    setClassesLoading(true);
    getClient()
      .request('classes', { sampleIds: ids })
      .then((res) => {
        if (cancelled) return;
        setClassesData(res);
        setClassesKey(classesReqKey);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setClassesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screen, loaded, classesReqKey, classesKey]);

  // Display order, applied on the main thread; the typed arrays are shared
  // with `classesData`, not copied.
  const orderedClassesData = useMemo(
    () => (classesData === null ? null : permuteClassesData(classesData, visibleSampleIds)),
    [classesData, visibleSampleIds],
  );

  // Both screens' selection controls speak only for the visible rows:
  // "Select all" adds them and leaves hidden selected ids alone, "Select
  // none" removes them and leaves the hidden ones.
  function selectAllVisible() {
    setSelected((prev) => new Set([...prev, ...visibleSampleIds]));
  }
  function selectNone() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of visibleSampleIds) next.delete(id);
      return next;
    });
  }

  // null follows the screen; a user toggle pins the value (see the header).
  const railIsCollapsed = railCollapsed ?? screen === 'genotypes';

  return (
    <div
      className="app-shell"
      data-density={density}
      data-rail={railIsCollapsed ? 'collapsed' : undefined}
    >
      <a
        href="#main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          mainRef.current?.focus();
        }}
      >
        Skip to main content
      </a>
      <Masthead />
      <Rail
        screen={screen}
        loaded={loaded !== null}
        collapsed={railIsCollapsed}
        onNavigate={setScreen}
        onToggleCollapsed={() => setRailCollapsed(!railIsCollapsed)}
      />
      <main className="app-main" id="main-content" ref={mainRef} tabIndex={-1}>
        {error !== null && (
          <p role="alert" className="alert">
            {error}
          </p>
        )}
        {screen === 'upload' && (
          <UploadScreen
            params={params}
            onParamsChange={(next) => void handleParamsChange(next)}
            busy={busy}
            onLoad={(r) => void runLoad(r)}
            onFetchCallSets={fetchCallSets}
            onCancelBrapi={cancelBrapi}
            brapiLoading={brapiLoading}
            loaded={loaded}
          />
        )}
        {screen === 'summary' && (
          <SummaryScreen
            loaded={loaded}
            qc={qc}
            segmentParams={params.segments}
            gapCriterion={gapCriterion}
          />
        )}
        {screen === 'lines' && (
          <LineTableScreen
            loaded={loaded}
            visibleRows={visibleLineRows}
            regions={regions}
            counts={counts}
            sort={lineSort}
            onSortChange={setLineSort}
            filter={lineFilter}
            onFilterChange={setLineFilter}
            selected={selected}
            onSelectionChange={setSelected}
            targetSpecs={targetSpecs}
            onApplyTargets={(specs) => void handleApplyTargets(specs)}
            onSelectAllVisible={selectAllVisible}
            onSelectNone={selectNone}
            density={density}
            onDensityChange={setDensity}
          />
        )}
        {screen === 'genotypes' && (
          <GenotypeViewScreen
            loaded={loaded}
            classesData={orderedClassesData}
            loading={classesLoading}
            counts={counts}
            sort={lineSort}
            onSortChange={setLineSort}
            filter={lineFilter}
            onFilterChange={setLineFilter}
            regions={regions}
            selected={selected}
            onSelectionChange={setSelected}
            onSelectAllVisible={selectAllVisible}
            onSelectNone={selectNone}
            onRequestMarkerDetail={(markerIndex, sampleIds) =>
              getClient().request('markerDetail', { markerIndex, sampleIds })
            }
          />
        )}
        {screen === 'compare' && (
          <CompareScreen
            samples={loaded?.samples ?? NO_SAMPLES}
            compare={compare}
            runCompare={(a, b, mode) => void runCompare(a, b, mode)}
            busy={busy}
            chromosomeOrder={loaded?.chromosomeOrder ?? NO_CHROMOSOMES}
          />
        )}
        {screen === 'export' && (
          <ExportScreen
            loaded={loaded}
            rpp={rpp}
            qc={qc}
            segmentsByCandidate={segmentsByCandidate}
            targets={targets}
            params={params}
            gapCriterion={gapCriterion}
            compare={compare}
            classesData={orderedClassesData}
            classesLoading={classesLoading}
            busy={busy}
            onRequestDiscordantMarkersCsv={(sampleA, sampleB, mode) =>
              getClient()
                .request('discordantMarkersCsv', { sampleA, sampleB, mode })
                .then((r) => r.csv)
            }
          />
        )}
        <Footer />
      </main>
    </div>
  );
}
